import { Lock, CreditCard, ShieldAlert } from "lucide-react";
import { useState, useEffect, useRef, FC } from "react";
import UserAvatar from "./user-avatar/UserAvatar";
import axios from "axios";
import { Tooltip, CircularProgress } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { unlockUserAccountAction } from "../../redux/actions/authAction";
import { GET_MANAGERS_WITH_ACCESS_CODE } from "../../configs/apiConfig";
import useNfcReader from "../../hooks/useNfcReader";

const MAX_ATTEMPTS = 5;

interface IManager {
  id: number;
  firstName: string;
  lastName: string;
  avatar: string | null;
}

const AvatarTile: FC<{
  manager: IManager;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
}> = ({ manager, selected, disabled, onClick }) => (
  <Tooltip title={`${manager.firstName} ${manager.lastName}`} arrow>
    <span style={{ display: "inline-flex" }}>
      <UserAvatar
        src={manager.avatar}
        name={`${manager.firstName} ${manager.lastName}`}
        id={manager.id}
        size={80}
        selected={selected}
        disabled={disabled}
        onClick={onClick}
      />
    </span>
  </Tooltip>
);

export default function AccountLockedModal() {
  const dispatch = useAppDispatch();
  const { user, unlockUserLoading } = useAppSelector((state) => state.auth);

  const [managers, setManagers] = useState<IManager[]>([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [selectedManagerId, setSelectedManagerId] = useState<number | null>(null);
  const [code, setCode] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [remainingAttempts, setRemainingAttempts] = useState<number>(MAX_ATTEMPTS);
  const [isBlocked, setIsBlocked] = useState(false);
  const codeInputRef = useRef<HTMLInputElement>(null);

  const rawNfcScan = useNfcReader();
  const token = localStorage.getItem("token");

  // Fetch managers with access codes
  useEffect(() => {
    setManagersLoading(true);
    axios
      .get(GET_MANAGERS_WITH_ACCESS_CODE, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setManagers(res.data?.body ?? []))
      .catch(() => setManagers([]))
      .finally(() => setManagersLoading(false));
  }, []);

  // Focus code input when a manager is selected
  useEffect(() => {
    if (selectedManagerId !== null) {
      setTimeout(() => codeInputRef.current?.focus(), 50);
    }
  }, [selectedManagerId]);

  // NFC auto-unlock (not subject to code attempt limit)
  useEffect(() => {
    if (!rawNfcScan || !user?.id || isBlocked) return;
    handleUnlock({ nfcCardId: rawNfcScan });
  }, [rawNfcScan]);

  const handleSelectManager = (id: number) => {
    if (isBlocked) return;
    setSelectedManagerId(id);
    setCode("");
    setErrorMsg(null);
  };

  /** Parse the sentinel error strings the backend returns */
  const parseBackendError = (raw: string) => {
    if (raw === "MAX_ATTEMPTS_EXCEEDED") {
      return { blocked: true, remaining: 0, message: null };
    }
    const match = raw.match(/^INVALID_CODE:(\d+)$/);
    if (match) {
      const rem = parseInt(match[1], 10);
      return { blocked: rem <= 0, remaining: rem, message: null };
    }
    return { blocked: false, remaining: remainingAttempts, message: raw };
  };

  const handleUnlock = async ({
    managerId,
    enteredCode,
    nfcCardId,
  }: {
    managerId?: number;
    enteredCode?: string;
    nfcCardId?: string;
  }) => {
    if (!user?.id || isBlocked) return;
    setErrorMsg(null);
    try {
      await dispatch(
        unlockUserAccountAction({ userId: user.id, managerId, enteredCode, nfcCardId })
      ).unwrap();
      // accountLocked → false in state; PrivateRoute unmounts this modal
    } catch (err: any) {
      const raw = typeof err === "string" ? err : (err?.message ?? "Invalid code. Please try again.");
      const parsed = parseBackendError(raw);

      if (parsed.blocked) {
        setIsBlocked(true);
        setRemainingAttempts(0);
        setErrorMsg(null);
      } else {
        setRemainingAttempts(parsed.remaining);
        setErrorMsg(parsed.message ?? `Incorrect code. ${parsed.remaining} attempt${parsed.remaining === 1 ? "" : "s"} remaining.`);
        setCode("");
        codeInputRef.current?.focus();
      }
    }
  };

  const selectedManager = managers.find((m) => m.id === selectedManagerId);
  const isDoneEnabled = !!(selectedManagerId && code.trim()) && !unlockUserLoading && !isBlocked;

  // ── Blocked state ──────────────────────────────────────────────────────────
  if (isBlocked) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-[9999]">
        <div className="w-[420px] bg-white rounded-2xl shadow-xl p-6">
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
              <ShieldAlert className="text-red-600" size={28} />
            </div>
            <h2 className="text-lg font-semibold text-gray-800 text-center">
              Access Suspended
            </h2>
            <p className="text-sm text-gray-600 text-center leading-relaxed">
              This account has been suspended after <span className="font-semibold text-red-600">{MAX_ATTEMPTS} failed</span> unlock attempts.
              <br /><br />
              Please contact your <span className="font-medium">system administrator</span> to reset access.
            </p>
            <div className="w-full h-px bg-gray-100 mt-2" />
            <p className="text-xs text-gray-400 text-center">
              Administrator must reset unlock attempts from the admin panel before this account can be unlocked.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Normal state ──────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-[9999]">
      <div className="w-[460px] bg-white rounded-2xl shadow-xl p-6 relative">
        {/* Header */}
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
            <Lock className="text-gray-600" size={18} />
          </div>
          <h2 className="text-lg font-semibold text-gray-800">Account Locked</h2>
        </div>

        {/* Attempt indicator */}
        <div className="flex gap-1.5 ml-[3.25rem] mb-4">
          {Array.from({ length: MAX_ATTEMPTS }).map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                i < MAX_ATTEMPTS - remainingAttempts ? "bg-red-400" : "bg-gray-200"
              }`}
            />
          ))}
        </div>

        {/* Message */}
        <p className="text-sm text-gray-600 leading-relaxed mb-5">
          This account has been temporarily locked by{" "}
          <span className="font-medium">Presto Valet Admin</span>. Select a
          manager below and enter their access code to unlock.
        </p>

        {/* Manager grid */}
        {managersLoading ? (
          <div className="flex justify-center py-6">
            <CircularProgress size={32} />
          </div>
        ) : managers.length === 0 ? (
          <p className="text-sm text-gray-400 italic text-center mb-4">
            No managers with access codes configured.
          </p>
        ) : (
          <div className="flex flex-wrap gap-3 justify-center mb-5">
            {managers.map((m) => (
              <AvatarTile
                key={m.id}
                manager={m}
                selected={selectedManagerId === m.id}
                disabled={false}
                onClick={() => handleSelectManager(m.id)}
              />
            ))}
          </div>
        )}

        {/* Code input */}
        {selectedManagerId !== null && (
          <div className="mb-4">
            <label className="text-sm text-gray-700 mb-1 block">
              Enter{" "}
              <span className="font-medium">
                {selectedManager?.firstName} {selectedManager?.lastName}
              </span>
              's access code
            </label>
            <input
              ref={codeInputRef}
              type="password"
              autoComplete="new-password"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setErrorMsg(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && isDoneEnabled)
                  handleUnlock({ managerId: selectedManagerId!, enteredCode: code });
              }}
              placeholder="Access code"
              className="w-full h-11 px-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#018752]"
              disabled={unlockUserLoading}
            />
          </div>
        )}

        {/* Error */}
        {errorMsg && (
          <p className="text-sm text-red-500 mb-3">{errorMsg}</p>
        )}

        {/* Divider */}
        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-xs text-gray-400">OR</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* NFC */}
        <div className="flex flex-col items-center justify-center gap-2 mb-5">
          <CreditCard size={26} className="text-gray-400" />
          <p className="text-sm text-gray-500">Tap Manager Access Badge</p>
        </div>

        {/* Done button */}
        <button
          disabled={!isDoneEnabled}
          onClick={() =>
            handleUnlock({ managerId: selectedManagerId!, enteredCode: code })
          }
          className={`w-full h-11 rounded-lg text-white font-medium transition-all ${
            isDoneEnabled
              ? "bg-[#018752] hover:opacity-90"
              : "bg-gray-300 cursor-not-allowed"
          }`}
        >
          {unlockUserLoading ? "Verifying…" : "Done"}
        </button>
      </div>
    </div>
  );
}
